import { Sprite } from 'pixi.js'
import gsap from 'gsap'
import { delay } from '@/scripts/helpers/animation'
import { getRandomInt } from '@/scripts/utils/math'

export async function animateCar(
  sprite: Sprite,
  startX: number,
  direction: 'left' | 'right',
  travelDistance: number,
  duration: number,
): Promise<void> {
  // Kill any previous tweens on the car and its wheels
  gsap.killTweensOf(sprite)
  const wheels = sprite.children.filter((c) => c.name?.startsWith('wheel')) as Sprite[]
  for (const w of wheels) gsap.killTweensOf(w)

  // Reset position
  sprite.x = startX
  const targetX = direction === 'right' ? startX + travelDistance : startX - travelDistance

  // Randomize drive duration
  let randomizedDuration = getRandomInt(duration - 1, duration + 1)
  if (randomizedDuration <= 0) randomizedDuration = 0.1

  // Wheels rotation based on vehicle
  const wheelTweens: gsap.core.Tween[] = []

  if (wheels.length) {
    const baseWheel = wheels[0]

    // Wheel radius from actual sprite width (anchor is 0.5 → true radius)
    let wheelRadius = Math.abs(baseWheel.width) * 0.5
    if (!wheelRadius || !Number.isFinite(wheelRadius)) {
      wheelRadius = 20 // fallback if texture not ready
    }

    const TWO_PI = Math.PI * 2
    const circumference = TWO_PI * wheelRadius

    // Distance the car actually travels
    const distance = Math.abs(targetX - startX)

    if (circumference > 0 && distance > 0) {
      // How many full rotations over the whole path?
      const rotations = distance / circumference

      // Flip-aware direction
      const flipped = sprite.scale.x < 0
      const desired = direction === 'right' ? +1 : -1
      const rotationDir = flipped ? -desired : desired

      // Total angle over the whole drive
      const totalAngle = rotationDir * rotations * TWO_PI

      wheels.forEach((w) => {
        wheelTweens.push(
          gsap.to(w, {
            rotation: `+=${totalAngle}`,
            duration: randomizedDuration,
            ease: 'linear',
          }),
        )
      })
    }
  }

  // Flip sign based on direction
  const sign = sprite.children.find((c) => c.name === 'sign') as Sprite | undefined
  if (sign) {
    sign.scale.x = direction === 'right' ? -1 : 1
  }

  return new Promise((resolve) => {
    gsap.to(sprite, {
      x: targetX,
      duration: randomizedDuration,
      ease: 'linear',
      onComplete: () => {
        wheelTweens.forEach((tw) => tw.kill())
        resolve()
      },
    })
  })
}

export async function animateTram(
  sprite: Sprite,
  startX: number,
  direction: 'left' | 'right',
  travelDistance: number,
  driveDuration: number,
  stopHoldSeconds: number,
): Promise<void> {
  gsap.killTweensOf(sprite)
  sprite.x = startX

  const tramStopPosition = 1300
  const exitX = direction === 'right' ? startX + travelDistance : startX - travelDistance

  await new Promise<void>((resolve) => {
    gsap.to(sprite, {
      x: tramStopPosition,
      duration: Math.max(
        0.1,
        Math.min(driveDuration, (Math.abs(tramStopPosition - startX) / travelDistance) * driveDuration),
      ),
      ease: 'sine.out',
      onComplete: resolve,
    })
  })

  await delay(stopHoldSeconds)

  await new Promise<void>((resolve) => {
    gsap.to(sprite, {
      x: exitX,
      duration: Math.max(
        0.1,
        Math.min(driveDuration, (Math.abs(exitX - tramStopPosition) / travelDistance) * driveDuration),
      ),
      ease: 'sine.in',
      onComplete: resolve,
    })
  })
}
